import { useState, useRef, useEffect } from 'react';

interface FeedSettingsProps {
  soundEnabled: boolean;
  desktopEnabled: boolean;
  isAudioPrimed: boolean;
  notificationPermission: NotificationPermission | 'unsupported';
  onToggleSound: () => void;
  onToggleDesktop: () => void;
}

export function FeedSettings({
  soundEnabled,
  desktopEnabled,
  isAudioPrimed,
  notificationPermission,
  onToggleSound,
  onToggleDesktop,
}: FeedSettingsProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Click-outside dismissal
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent): void => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="text-gray-400 hover:text-white text-sm p-0.5 rounded transition-colors"
        title="Feed settings"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-dark-surface border border-dark-border rounded-lg shadow-xl z-50 p-3 space-y-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Notifications</h4>

          {/* Sound toggle */}
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-300">Sound</span>
            <button
              onClick={onToggleSound}
              className={`relative w-8 h-4 rounded-full transition-colors ${soundEnabled ? 'bg-accent-yellow' : 'bg-dark-border'}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${soundEnabled ? 'translate-x-4' : ''}`}
              />
            </button>
          </label>

          {/* Desktop toggle */}
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-300">Desktop</span>
            <button
              onClick={onToggleDesktop}
              className={`relative w-8 h-4 rounded-full transition-colors ${desktopEnabled ? 'bg-accent-yellow' : 'bg-dark-border'}`}
              disabled={notificationPermission === 'denied'}
              title={notificationPermission === 'denied' ? 'Notifications blocked in browser settings' : undefined}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${desktopEnabled ? 'translate-x-4' : ''}`}
              />
            </button>
          </label>

          {notificationPermission === 'denied' && (
            <p className="text-xs text-bear-red">Notifications blocked in browser settings</p>
          )}

          {/* Audio priming warning */}
          {soundEnabled && !isAudioPrimed && (
            <div className="flex items-center gap-2 text-xs text-accent-yellow">
              <span className="w-2 h-2 rounded-full bg-accent-yellow animate-pulse flex-shrink-0" />
              <span>Click anywhere to activate audio</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
